import { useCallback, useState } from 'react';
import { format } from 'date-fns';
import {
  downloadSupplierStatsExport,
  SupplierStatsExportKind,
} from '../services/supplierStatsService';
import {
  BrandSortBy,
  ProductSortBy,
  SupplierSortBy,
  SortOrder,
  SupplierStatsGranularity,
} from '../types/supplierStats';

interface UseSupplierStatsExportOptions {
  kind: SupplierStatsExportKind;
  startDate: Date;
  endDate: Date;
  search?: string;
  sortBy?: BrandSortBy | SupplierSortBy | ProductSortBy;
  sortOrder?: SortOrder;
  granularity?: SupplierStatsGranularity;
  categoryId?: number | null;
  supplierId?: number;
  brandName?: string;
  productId?: number;
  stockFilter?: string;
}

export function useSupplierStatsExport({
  kind,
  startDate,
  endDate,
  search,
  sortBy,
  sortOrder,
  granularity,
  categoryId,
  supplierId,
  brandName,
  productId,
  stockFilter,
}: UseSupplierStatsExportOptions) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadSupplierStatsExport(
        kind,
        {
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          search: search || undefined,
          sortBy,
          sortOrder,
          granularity,
          categoryId: categoryId || undefined,
          stockFilter,
          supplierId,
        },
        {
          supplierId,
          brandName,
          productId,
        },
      );
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: Blob;
          status?: number;
        };
      };

      if (axiosErr?.response?.data instanceof Blob) {
        try {
          const text = await axiosErr.response.data.text();
          const parsed = JSON.parse(text) as {
            message?: string;
            error?: string;
          };
          setExportError(
            parsed.message ||
              parsed.error ||
              'Kunde inte exportera till Excel.',
          );
          return;
        } catch {
          // fall through
        }
      }

      setExportError('Kunde inte exportera till Excel.');
    } finally {
      setExporting(false);
    }
  }, [
    brandName,
    categoryId,
    endDate,
    granularity,
    kind,
    productId,
    search,
    sortBy,
    sortOrder,
    startDate,
    supplierId,
    stockFilter,
  ]);

  return {
    exporting,
    exportError,
    handleExport,
    clearExportError: () => setExportError(null),
  };
}
